/**
 * @fileoverview Which deployment is this code running in?
 * @module lib/features/core/flagEnvironment
 *
 * A customer can write `environment eq production` in the flag editor. Before
 * this file existed nothing filled the field in, so that rule compiled fine and
 * then never matched — a rule type that lies, which is the failure mode the
 * platform-only refusal list exists to prevent elsewhere.
 *
 * The browser can answer this honestly, from the host it was loaded on. It is a
 * guess, so it is a *documented* guess with an override
 * (`init({ flags: { environment } })`) rather than a hidden one, and it is
 * deliberately conservative: anything we do not recognise as local or as a
 * preview host is `production`. Guessing `development` for a real customer
 * hostname would silently disable a production rollout, which is the expensive
 * direction to be wrong in.
 *
 * Kept free of the transport so a test — and a server runtime — can import it.
 */

export type FlagEnvironment = 'development' | 'preview' | 'production';

/**
 * Hosts that are a developer's own machine. Exact matches and suffixes both, so
 * `myapp.localhost` and the `.local` mDNS names a phone-on-the-LAN test uses
 * are covered.
 */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]']);
const LOCAL_SUFFIXES = ['.localhost', '.local', '.test', '.internal', '.localdomain'];

/** RFC1918 and link-local — a dev server reached from another device on the LAN. */
const PRIVATE_IP = /^(10\.|127\.|169\.254\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/;

/**
 * Hosts that belong to a preview deployment.
 *
 * Two shapes: the platforms that hand out per-branch URLs, and the naming
 * conventions teams use for their own staging. Both are suffix/segment matches,
 * never substring — `mypreview.com` is a production site and must not be caught
 * by looking for "preview" anywhere in the string.
 */
const PREVIEW_SUFFIXES = [
  '.vercel.app',
  '.netlify.app',
  '.pages.dev',
  '.onrender.com',
  '.fly.dev',
  '.herokuapp.com',
  '.ngrok.io',
  '.ngrok-free.app',
  '.trycloudflare.com',
  '.githubpreview.dev',
  '.app.github.dev',
  '.gitpod.io',
  '.repl.co',
  '.replit.dev',
  '.webcontainer.io',
  '.csb.app',
];

/** Leading labels that mean "not the real thing" by near-universal convention. */
const PREVIEW_LABELS = new Set([
  'staging', 'stage', 'preview', 'preprod', 'pre-prod', 'uat',
  'qa', 'test', 'testing', 'dev', 'develop', 'development', 'sandbox', 'demo',
]);

/**
 * Classify a hostname.
 *
 * Exported separately from `detectEnvironment` so it can be tested without a
 * `window`, and so a server binding can classify a host it was handed.
 */
export function environmentForHost(rawHost: string | null | undefined): FlagEnvironment {
  const host = (rawHost ?? '').trim().toLowerCase().replace(/:\d+$/, '');
  if (!host) return 'production';

  if (LOCAL_HOSTS.has(host)) return 'development';
  if (LOCAL_SUFFIXES.some((suffix) => host.endsWith(suffix))) return 'development';
  if (PRIVATE_IP.test(host)) return 'development';

  if (PREVIEW_SUFFIXES.some((suffix) => host.endsWith(suffix))) return 'preview';

  // Only the first label, and only as a whole label. `staging.acme.com` is a
  // preview; `stagingsupplies.com` is somebody's business.
  const [first] = host.split('.');
  if (first && PREVIEW_LABELS.has(first)) return 'preview';

  return 'production';
}

/**
 * The environment this SDK is running in, or `undefined` when there is nothing
 * to read it from (a worker, a server runtime, a test harness).
 *
 * `undefined` rather than a guess: a missing context field never matches a rule,
 * which is the safe direction. Inventing `production` off-browser would make
 * `environment eq production` fire inside a build script.
 */
export function detectEnvironment(): FlagEnvironment | undefined {
  try {
    if (typeof location === 'undefined' || !location?.hostname) return undefined;
    return environmentForHost(location.hostname);
  } catch {
    return undefined;
  }
}
